import React from 'react';
import { InputNumber, Select, Button, Divider, Checkbox, Alert } from 'antd';
import { PlusOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import ConfigurationLayout from '../configuration.layout';
import styles from './styles/fee-structure-step.module.scss';

const { Option } = Select;

function FeeStructureStep({
    state,
    firstTimeSetup,
    setFee,
    setStandardFreeAdvanceTime,
    setFeeInstance,
    setFeeStrategy,
    addVolumeTierRule,
    removeVolumeTierRule,
    updateVolumeTierRule,
    addFreeAdvanceRule,
    removeFreeAdvanceRule,
    updateFreeAdvanceRule,
    toggleFeeSection,
    hasChanges,
    onSubmit,
    onContinue,
    onCancel,
    onStepChange
}) {
    const { fee = 3.0, fee_policy, fee_rules, fee_enabled = {}, loading, user } = state;
    const { standardFreeAdvanceTime = 1, feeInstance = 'on-advance', feeStrategy = 'on-top-of-advance' } = fee_policy || {};
    const { volumeTierRules = [{ id: 1, condition: 'less-than-50', fee: 1.0 }], freeAdvanceRules = [{ id: 1, count: 1, condition: 'first-advance' }] } =
        fee_rules || {};
    const { instantFee = true, volumeTiers = true, freeAdvances = true } = fee_enabled;

    // Main content - form section
    const mainContent = (
        <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>Configure your EWA monetization</h2>

            <div className={styles.formFields}>
                {/* Instant Fee per Advance */}
                <div className={`${styles.sectionCard} ${!instantFee ? styles.sectionDisabled : ''}`}>
                    <div className={styles.sectionHeader}>
                        <Checkbox checked={instantFee} onChange={() => toggleFeeSection('instantFee')}>
                            <span className={styles.fieldLabel}>Instant Transfer Fee per Advance</span>
                        </Checkbox>
                        <p className={styles.fieldDescription}>The fee your users will pay your institution to advance wages instantly</p>
                    </div>
                    <div className={styles.sectionBody}>
                        <div className={styles.inputWithPrefix}>
                            <span className={styles.currencyPrefix}>$</span>
                            <InputNumber
                                value={fee}
                                onChange={setFee}
                                min={0}
                                max={999.99}
                                step={0.01}
                                precision={2}
                                className={styles.feeInput}
                                disabled={!instantFee}
                            />
                        </div>
                    </div>
                </div>

                {/* Standard Free Advance Time Lapse */}
                <div className={styles.sectionCard}>
                    <div className={styles.sectionHeaderPlain}>
                        <label className={styles.fieldLabel}>Standard Free Advance Time Lapse</label>
                        <p className={styles.fieldDescription}>The time users will wait to receive funds when they request a standard free advance</p>
                    </div>
                    <div className={styles.timeInputGroup}>
                        <InputNumber value={standardFreeAdvanceTime} onChange={setStandardFreeAdvanceTime} min={1} max={999} className={styles.timeInput} />
                        <Select className={styles.timeUnitSelect} value="business-days" disabled>
                            <Option value="business-days">business days</Option>
                        </Select>
                    </div>
                </div>
                
                {/* Standard Free Advance Time Lapse */}
                <div className={styles.sectionCard}>
                    <div className={styles.sectionHeaderPlain}>
                        <label className={styles.fieldLabel}>Fee Strategy</label>
                        <p className={styles.fieldDescription}>Choose the option of charging your fee at the moment of advance or repayment</p>
                    </div>
                    <Select value={feeStrategy} onChange={setFeeStrategy} className={styles.instanceSelect}>
                        <Option value="on-top-of-advance">On Top of Advance</Option>
                        <Option value="reduce-from-advance">Reduce from Advance</Option>
                    </Select>
                </div>

                {/* Instance to Charge Your Fee */}
                <div className={styles.sectionCard}>
                    <div className={styles.sectionHeaderPlain}>
                        <label className={styles.fieldLabel}>Instance to Charge Your Fee</label>
                        <p className={styles.fieldDescription}>Choose the option of charging your fee at the moment of advance or repayment</p>
                    </div>
                    <Select value={feeInstance} onChange={setFeeInstance} className={styles.instanceSelect}>
                        <Option value="on-advance">On Advance</Option>
                        <Option value="on-repayment">On Repayment</Option>
                    </Select>
                </div>
            </div>

            {/* Advanced Fee Structure Configurations */}
            <div className={styles.advancedSection}>
                <h3 className={styles.advancedTitle}>
                    Advanced fee structure configurations
                    <span className={styles.optionalTag}>Optional</span>
                </h3>
                <p className={styles.advancedDescription}>Add all of the applicable configuration for your monetization strategy</p>

                <div className={`${styles.configurationCard} ${!volumeTiers ? styles.sectionDisabled : ''}`}>
                    <div className={styles.configurationHeaderRow}>
                        <Checkbox checked={volumeTiers} onChange={() => toggleFeeSection('volumeTiers')}>
                            <span className={styles.configurationTitle}>Fee by Volume Tiers</span>
                        </Checkbox>
                    </div>
                    <Alert
                        type="warning"
                        showIcon
                        message={`Advances that fall out of the volume segmentation will have your default Instant Transfer Fee (${new Intl.NumberFormat(
                            'en-US',
                            {
                                style: 'currency',
                                currency: 'USD'
                            }
                        ).format(Number(fee) || 0)})`}
                        className={styles.volumeAlert}
                    />
                    {volumeTiers &&
                        volumeTierRules.map((rule) => (
                            <div key={rule.id} className={styles.ruleRow}>
                                <div className={styles.ruleTextGroup}>
                                    <span className={styles.ruleText}>If the advance is from</span>
                                    <Select
                                        value={rule.condition}
                                        onChange={(value) => updateVolumeTierRule(rule.id, 'condition', value)}
                                        className={styles.conditionSelect}
                                        disabled={!volumeTiers}>
                                        <Option value="less-than-50">$0 - $50</Option>
                                        <Option value="less-than-75">$51 - $75</Option>
                                        <Option value="less-than-100">$76 - $100</Option>
                                        <Option value="less-than-200">$101 - $200</Option>
                                        <Option value="less-than-500">$201 - $500</Option>
                                    </Select>
                                </div>
                                <div className={styles.ruleActionGroup}>
                                    <span className={styles.ruleText}>then the advance fee is</span>
                                    <div className={styles.feeInputGroup}>
                                        <span className={styles.currencyPrefix}>$</span>
                                        <InputNumber
                                            value={rule.fee}
                                            onChange={(value) => updateVolumeTierRule(rule.id, 'fee', value)}
                                            min={0}
                                            max={999.99}
                                            step={0.01}
                                            precision={2}
                                            className={styles.feeInput}
                                            disabled={!volumeTiers}
                                        />
                                    </div>
                                </div>
                                <Button
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    className={styles.deleteButton}
                                    onClick={() => removeVolumeTierRule(rule.id)}
                                    disabled={!volumeTiers}
                                />
                            </div>
                        ))}
                    {volumeTiers && (
                        <Button type="text" icon={<PlusOutlined />} className={styles.addRuleButton} onClick={addVolumeTierRule}>
                            Add threshold setting row
                        </Button>
                    )}
                </div>

                <div className={`${styles.configurationCard} ${!freeAdvances ? styles.sectionDisabled : ''}`}>
                    <div className={styles.configurationHeaderRow}>
                        <Checkbox checked={freeAdvances} onChange={() => toggleFeeSection('freeAdvances')}>
                            <span className={styles.configurationTitle}>Free Advances</span>
                        </Checkbox>
                    </div>
                    {freeAdvances &&
                        freeAdvanceRules.map((rule) => (
                            <div key={rule.id} className={styles.ruleRow}>
                                <div className={styles.ruleTextGroup}>
                                    <span className={styles.ruleText}>First</span>
                                    <InputNumber
                                        value={rule.count}
                                        onChange={(value) => updateFreeAdvanceRule(rule.id, 'count', value)}
                                        min={1}
                                        max={999}
                                        className={styles.countInput}
                                        disabled={!freeAdvances}
                                    />
                                    <span className={styles.ruleText}>advances are free</span>
                                </div>
                                <div className={styles.ruleActionGroup}>
                                    <span className={styles.ruleText}>at</span>
                                    <Select
                                        value={rule.condition}
                                        onChange={(value) => updateFreeAdvanceRule(rule.id, 'condition', value)}
                                        className={styles.conditionSelect}
                                        disabled={!freeAdvances}>
                                        <Option value="first-advance">first advance</Option>
                                        <Option value="monthly">monthly</Option>
                                        <Option value="weekly">weekly</Option>
                                        <Option value="daily">daily</Option>
                                    </Select>
                                </div>
                                <Button
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    className={styles.deleteButton}
                                    onClick={() => removeFreeAdvanceRule(rule.id)}
                                    disabled={!freeAdvances}
                                />
                            </div>
                        ))}
                    {freeAdvances && (
                        <Button type="text" icon={<PlusOutlined />} className={styles.addRuleButton} onClick={addFreeAdvanceRule}>
                            Add threshold setting row
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );

    // Side content - summary section
    const sideContent = (
        <div className={styles.sideContent}>
            <div className={styles.userProfile}>
                <div className={styles.userAvatar}>
                    <img src="/user.png" alt="User" className={styles.avatarImage} />
                </div>
                <div className={styles.userName}>Meet Michael,</div>
            </div>

            <div className={styles.summarySection}>
                <h3 className={styles.summaryTitle}>Advance Summary</h3>

                <div className={styles.summaryBlock}>
                    <h4 className={styles.summaryGroupTitle}>Advance</h4>
                    <div className={styles.summaryRow}>
                        <span className={styles.summaryLabel}>Requested Advance Amount</span>
                        <span className={styles.summaryValue}>$78.00</span>
                    </div>
                    <div className={styles.summaryRow}>
                        <span className={styles.summaryLabel}>Advance Fee</span>
                        <span className={styles.summaryValueRed}>-${(Number(fee) || 0).toFixed(2)}</span>
                    </div>
                    <div className={styles.summaryRow}>
                        <span className={styles.summaryLabel}>Michael Received</span>
                        <span className={styles.summaryValueBlue}>${(78 - (Number(fee) || 0)).toFixed(2)}</span>
                    </div>
                    <div className={styles.summaryInfo}>
                        <InfoCircleOutlined className={styles.infoIcon} />
                        <span className={styles.infoText}>
                            Michael received less money than he requested due to your "{feeInstance === 'on-advance' ? 'On Advance' : 'On Repayment'}" fee
                            charge instance set up
                        </span>
                    </div>
                </div>

                <div className={styles.summaryBlock}>
                    <h4 className={styles.summaryGroupTitle}>Repayment</h4>
                    <div className={styles.summaryRow}>
                        <span className={styles.summaryLabel}>Received Amount</span>
                        <span className={styles.summaryValue}>${(78 - (Number(fee) || 0)).toFixed(2)}</span>
                    </div>
                    <div className={styles.summaryRow}>
                        <span className={styles.summaryLabel}>Advance Fee Payment</span>
                        <span className={styles.summaryValueGreen}>${(Number(fee) || 0).toFixed(2)}</span>
                    </div>
                    <div className={styles.summaryRow}>
                        <span className={styles.summaryLabel}>Michael Repays</span>
                        <span className={styles.summaryValueBlue}>$78.00</span>
                    </div>
                </div>
            </div>

            {!firstTimeSetup && hasChanges() && (
                <div className={styles.actionSection}>
                    <Divider className={styles.actionDivider} />
                    <div className={styles.actionButtons}>
                        <Button size="large" className={styles.cancelButton} onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button type="primary" size="large" loading={loading} onClick={onSubmit} className={styles.continueButton}>
                            Submit
                        </Button>
                    </div>
                </div>
            )}
            {firstTimeSetup && (
                <div className={styles.actionSection}>
                    <Divider className={styles.actionDivider} />
                    <div className={styles.actionButtons}>
                        <Button size="large" className={styles.cancelButton} onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button type="primary" size="large" loading={loading} onClick={onContinue} className={styles.continueButton}>
                            Continue
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <ConfigurationLayout isFirstTimeSetUp={firstTimeSetup} currentStep={2} sideContent={sideContent} onStepChange={onStepChange} user={user}>
            {mainContent}
        </ConfigurationLayout>
    );
}

export default FeeStructureStep;
